// Case Buddy domain module generated from CaseBuddyCore.js.
// Spreadsheet-shaped rows remain the source of truth.
import { buildBridgeInSystemNote, buildBridgeOutSystemNote } from './CaseBuddyBridgeServices.js';
import {
  buildMainTransactionBlocks,
  buildRawSetSignature,
  buildTool68InputRowsForRawSet,
  normalizeTool68ChainMode,
  normalizeTool68ProcessedRowsForMainRewrite,
  runTool68BFlowAnalysis
} from './CaseBuddyRawPreparation.js';
import {
  abbreviateCaseBuddyHash,
  buildTsv,
  firstNonEmptyEntryValue,
  firstNonEmptyValue,
  normalizeCell,
  normalizeComparisonKey,
  rowHasMeaningfulData
} from './CaseBuddyShared.js';
import {
  buildRawDataSetsFromMatrix,
  isRawRootCandidateMatrixRow,
  normalizeCaseRowsToWidth,
  normalizeRawDataImportRowsFromPaste,
  parseCaseEntries,
  rowLooksLikeCaseBuddyHeader,
  trimOuterEmptyRows
} from './CaseBuddySheetData.js';
import { CASE_COLUMN_HEADERS, CASE_DEFAULT_COLS, CASE_DEFAULT_ROWS, createCaseSheetState, normalizeCaseMatrix } from './CaseBuddyWorkbookState.js';

export const CASE_ADDRESS_COLOR_TOOL_RUN_ID = 'color_case_address_cells';

export const CASE_ADDRESS_COLOR_CLEAR_TOOL_RUN_ID = 'clear_case_address_colors';

export const CASE_ADDRESS_COLOR_CLASS = 'case-address-coloring-v1';

export const CASE_ADDRESS_COLOR_COLS = [2, 3];

export const CASE_ADDRESS_COLOR_PALETTE = Object.freeze([
  '#bfdbfe', '#bbf7d0', '#fde68a', '#ddd6fe', '#fed7aa', '#a7f3d0',
  '#fbcfe8', '#bae6fd', '#c7d2fe', '#d9f99d', '#e0e7ff', '#fef3c7',
  '#ccfbf1', '#e9d5ff', '#fce7f3', '#dcfce7'
]);

export function normalizePathOperationText(value) {
  return normalizeCell(value).replace(/\s+/g, ' ').trim();
}

export function normalizePathOperationKey(value) {
  return normalizePathOperationText(value).toLowerCase();
}

export function parseCaseContinuesAsHashes(value = '') {
  const text = normalizePathOperationText(value);
  if (!text) return [];
  const normalized = text.replace(/^continues\s+as\s*/i, '').replace(/^branches\s+as\s*/i, '');
  if (/^path\s+ends\.?$/i.test(normalized)) return [];
  return normalized
    .split(/[,;\n]/)
    .map(part => part.replace(/^continues\s+as\s*/i, '').replace(/^branches\s+as\s*/i, '').trim())
    .map(part => part.replace(/[.。]+$/g, '').trim())
    .filter(Boolean);
}

export function rowHasFundsFollowedAndLeftBounds(row = []) {
  return !!normalizeCell(row?.[12]) && !!(normalizeCell(row?.[17]) || normalizeCell(row?.[18]));
}

export function rowIsCaseBuddyPathEnding(row = []) {
  return /\bpath\s+ends\b/i.test(normalizeCell(row?.[16]));
}

export function getEntryEndRowIndex(entry = {}) {
  const rows = Array.isArray(entry.rows) ? entry.rows : [];
  if (rows.length === 0) return entry.startRow ?? -1;
  return rows.reduce((max, item) => Number.isInteger(item?.rowIndex) ? Math.max(max, item.rowIndex) : max, entry.startRow ?? -1);
}

export function entryContainsRow(entry = {}, rowIndex = -1) {
  return Array.isArray(entry.rows) && entry.rows.some(item => item?.rowIndex === rowIndex);
}

export function getFirstPathOperationEntryLabel(entry = {}, fallbackRow = []) {
  const entryRows = Array.isArray(entry.rows) ? entry.rows : [];
  return firstNonEmptyEntryValue(entryRows, 0) || normalizeCell(fallbackRow?.[0]);
}

export function getFirstPathOperationEntryAttribution(entry = {}, fallbackRow = []) {
  const entryRows = Array.isArray(entry.rows) ? entry.rows : [];
  return normalizeCell(fallbackRow?.[10]) || firstNonEmptyEntryValue(entryRows, 10);
}

export function findMainBlockForRow(mainBlocks = [], rowIndex = -1) {
  return (Array.isArray(mainBlocks) ? mainBlocks : []).find((block) => {
    if (!Number.isInteger(rowIndex)) return false;
    const start = Number.isInteger(block?.startRowIndex) ? block.startRowIndex : -1;
    const end = Number.isInteger(block?.replaceEndRowIndex) ? block.replaceEndRowIndex : block.contentEndRowIndex;
    return rowIndex >= start && rowIndex <= end;
  }) || null;
}

export function getRawSetRootEntryRowsForPathOperation(rawSet = {}) {
  const rows = Array.isArray(rawSet?.rows) ? rawSet.rows : [];
  if (rows.length === 0) return [];

  const rootHashKey = normalizePathOperationKey(rawSet?.rootHash);
  const rootIndex = rows.findIndex(row => isRawRootCandidateMatrixRow(row) && (!rootHashKey || normalizePathOperationKey(row?.[1]) === rootHashKey));
  if (rootIndex < 0) {
    const rootRow = Array.isArray(rawSet?.rootRow) ? rawSet.rootRow : rows.find(isRawRootCandidateMatrixRow);
    return rootRow ? [rootRow] : [];
  }

  const entryRows = [];
  for (let index = rootIndex; index < rows.length; index += 1) {
    const row = rows[index];
    if (index > rootIndex && (rowLooksLikeCaseBuddyHeader(row) || normalizeCell(row?.[1]))) break;
    if (rowHasMeaningfulData(row)) entryRows.push(row);
  }

  return entryRows;
}

export function pathOperationOutputRowMatches(candidateRow = [], rawOutputRow = []) {
  const rowToKey = normalizePathOperationKey(candidateRow?.[3]);
  const rowFundsKey = normalizePathOperationKey(candidateRow?.[12]);
  const rowLeftNumericKey = normalizePathOperationKey(candidateRow?.[18]);
  const rowLeftDisplayKey = normalizePathOperationKey(candidateRow?.[17]);

  const rawToKey = normalizePathOperationKey(rawOutputRow?.[3]);
  const rawFundsKey = normalizePathOperationKey(rawOutputRow?.[12]);
  const rawLeftNumericKey = normalizePathOperationKey(rawOutputRow?.[18]);
  const rawLeftDisplayKey = normalizePathOperationKey(rawOutputRow?.[17]);

  if (rowToKey && rowToKey !== rawToKey) return false;
  if (rowFundsKey && rowFundsKey !== rawFundsKey) return false;
  if (rowLeftNumericKey && rowLeftNumericKey !== rawLeftNumericKey) return false;
  if (!rowLeftNumericKey && rowLeftDisplayKey && rowLeftDisplayKey !== rawLeftDisplayKey) return false;

  return true;
}

export function findMatchingRawSetRootEntryRow(rawSet = {}, candidateRow = []) {
  return getRawSetRootEntryRowsForPathOperation(rawSet).find(row => pathOperationOutputRowMatches(candidateRow, row)) || null;
}

export function rawSetMatchesPathOperationCandidate(rawSet = {}, candidateRow = [], hash = '') {
  const hashMatch = normalizePathOperationKey(rawSet?.rootHash) === normalizePathOperationKey(hash);
  if (!hashMatch) return false;
  return !!findMatchingRawSetRootEntryRow(rawSet, candidateRow);
}

export function decorateRawSetForPathOperation(rawSet = {}, candidateRow = [], hash = '') {
  const rootRow = Array.isArray(rawSet?.rootRow) ? rawSet.rootRow : (Array.isArray(rawSet?.rows) ? rawSet.rows.find(isRawRootCandidateMatrixRow) : []);
  const matchedRootEntryRow = findMatchingRawSetRootEntryRow(rawSet, candidateRow) || rootRow;
  const labels = [
    normalizePathOperationKey(candidateRow?.[3]) ? 'To match' : 'hash match',
    normalizePathOperationKey(candidateRow?.[12]) ? 'funds match' : 'funds not required',
    normalizePathOperationKey(candidateRow?.[18]) || normalizePathOperationKey(candidateRow?.[17]) ? 'left bounds match' : 'left bounds not required'
  ];
  return {
    ...rawSet,
    rootRow,
    matchedRootEntryRow,
    matchLabel: `${abbreviateCaseBuddyHash(hash)} · ${labels.join(' · ')}`
  };
}

export function buildPathOperationAnalysis(mainData = [], rawData = []) {
  const data = normalizeCaseMatrix(mainData);
  const rawSets = buildRawDataSetsFromMatrix(rawData).map((set) => {
    const rootRow = Array.isArray(set.rows) ? set.rows.find(isRawRootCandidateMatrixRow) : [];
    return { ...set, rootRow };
  });
  const entries = parseCaseEntries(data);
  const { blocks } = buildMainTransactionBlocks(data);
  const entriesByRow = new Map();
  entries.forEach((entry) => {
    (entry.rows || []).forEach((item) => entriesByRow.set(item.rowIndex, entry));
  });

  const mainGroupsById = new Map();
  const candidates = [];

  entries.forEach((entry) => {
    const entryStart = Number.isInteger(entry.startRow) ? entry.startRow : -1;
    const block = findMainBlockForRow(blocks, entryStart);
    if (!block) return;

    const mainLead = block.leadRow || [];
    const mainPathLabel = normalizeCell(mainLead?.[0]);
    const mainHash = normalizeCell(mainLead?.[1]);
    const mainGroupId = `main-${block.startRowIndex}`;
    if (!mainGroupsById.has(mainGroupId)) {
      mainGroupsById.set(mainGroupId, {
        id: mainGroupId,
        startRowIndex: block.startRowIndex,
        label: `${mainPathLabel || `Row ${block.spreadsheetRowNumber}`} · ${abbreviateCaseBuddyHash(mainHash)}`
      });
    }

    (entry.rows || []).forEach((item) => {
      const row = Array.isArray(item?.row) ? item.row : [];
      if (!rowHasFundsFollowedAndLeftBounds(row)) return;

      const hash = normalizeCell(entry.hash || row?.[1]);
      const childHashes = parseCaseContinuesAsHashes(row?.[16]);
      const isPathEnding = rowIsCaseBuddyPathEnding(row);
      const pathLabel = normalizeCell(row?.[0]) || getFirstPathOperationEntryLabel(entry, row);
      const rawCandidates = rawSets
        .filter(rawSet => rawSetMatchesPathOperationCandidate(rawSet, row, hash))
        .map(rawSet => decorateRawSetForPathOperation(rawSet, row, hash))
        .sort((left, right) => (right.rootRowIndex || 0) - (left.rootRowIndex || 0));
      const stateLabel = isPathEnding
        ? 'Path ending'
        : childHashes.length > 0
          ? `Continues to ${childHashes.length}`
          : 'Qualified output';

      candidates.push({
        id: `path-op-${item.rowIndex}-${normalizeComparisonKey(hash) || 'blank'}-${candidates.length}`,
        rowIndex: item.rowIndex,
        spreadsheetRowNumber: item.rowIndex + 1,
        entryStartRowIndex: entry.startRow,
        entryEndRowIndex: getEntryEndRowIndex(entry),
        mainBlockStartRowIndex: block.startRowIndex,
        mainBlockEndRowIndex: block.replaceEndRowIndex,
        mainBlockContentEndRowIndex: block.contentEndRowIndex,
        mainGroupId,
        mainPathLabel,
        pathLabel,
        hash,
        shortHash: abbreviateCaseBuddyHash(hash),
        fromAddress: normalizeCell(row?.[2]),
        toAddress: normalizeCell(row?.[3]),
        shortToAddress: abbreviateCaseBuddyHash(row?.[3]),
        utcDate: normalizeCell(row?.[4]) || firstNonEmptyEntryValue(entry.rows, 4),
        amount: normalizeCell(row?.[5]),
        token: normalizeCell(row?.[6]),
        systemNotes: normalizeCell(row?.[9]),
        attribution: getFirstPathOperationEntryAttribution(entry, row),
        analystNotes: normalizeCell(row?.[11]),
        fundsFollowed: normalizeCell(row?.[12]),
        priorBalance: normalizeCell(row?.[13]),
        fromHash: normalizeCell(row?.[14]),
        continuesAs: normalizeCell(row?.[16]),
        leftBoundsDisplay: normalizeCell(row?.[17]),
        leftBoundsNumeric: normalizeCell(row?.[18]),
        chainId: normalizeCell(row?.[19]) || normalizeCell(mainLead?.[19]),
        chainName: normalizeCell(row?.[20]) || normalizeCell(mainLead?.[20]),
        hopLogic: normalizeCell(row?.[21]),
        childHashes,
        isPathEnding,
        canPrune: true,
        canGraft: isPathEnding,
        canBridge: isPathEnding,
        stateLabel,
        rawCandidates,
        row: row.slice()
      });
    });
  });

  return {
    entries,
    entriesByRow,
    blocks,
    rawSets,
    candidates,
    mainGroups: Array.from(mainGroupsById.values()).sort((left, right) => left.startRowIndex - right.startRowIndex),
    qualifiedCount: candidates.length,
    pathEndingCount: candidates.filter(candidate => candidate.isPathEnding).length,
    graftReadyCount: candidates.filter(candidate => candidate.isPathEnding && candidate.rawCandidates.length > 0).length
  };
}

export function findPathOperationCandidateForRow(analysis = {}, rowIndex = -1) {
  if (!Number.isInteger(rowIndex)) return null;
  const candidates = Array.isArray(analysis?.candidates) ? analysis.candidates : [];
  return candidates.find(candidate => candidate.rowIndex === rowIndex)
    || candidates.find(candidate => rowIndex >= candidate.entryStartRowIndex && rowIndex <= candidate.entryEndRowIndex)
    || null;
}

export function findExactPathOperationCandidateForRow(analysis = {}, rowIndex = -1) {
  if (!Number.isInteger(rowIndex)) return null;
  const candidates = Array.isArray(analysis?.candidates) ? analysis.candidates : [];
  return candidates.find(candidate => candidate.rowIndex === rowIndex) || null;
}

export function normalizeCaseAddressColorKey(value) {
  return normalizeCell(value).toLowerCase();
}

export function caseClassTokens(value) {
  return new Set(String(value || '').split(/\s+/).filter(Boolean));
}

export function caseAddressColorMarkerPresent(tokens = new Set()) {
  return tokens.has(CASE_ADDRESS_COLOR_CLASS) || tokens.has('case-address-color-v1');
}

export function caseCellClassMapFromState(state = {}) {
  return new Map(Array.isArray(state?.cellClasses) ? state.cellClasses : []);
}

export function caseClassListWithoutAddressColors(tokens = new Set()) {
  const hasAddressColorMarker = caseAddressColorMarkerPresent(tokens);
  return [...tokens].filter((token) => {
    if (token === CASE_ADDRESS_COLOR_CLASS) return false;
    if (token.startsWith('case-address-color-') || token.startsWith('case-address-text-')) return false;
    if (hasAddressColorMarker && (token.startsWith('color-') || token.startsWith('text-'))) return false;
    return true;
  });
}

export function collectCaseAddressColorCells(mainData = []) {
  const data = normalizeCaseMatrix(mainData);
  const cells = [];

  data.forEach((row, rowIndex) => {
    if (rowIndex === 0 || rowLooksLikeCaseBuddyHeader(row)) return;

    CASE_ADDRESS_COLOR_COLS.forEach((colIndex) => {
      const value = normalizeCell(row?.[colIndex]);
      const key = normalizeCaseAddressColorKey(value);
      if (!key) return;

      cells.push({
        row: rowIndex,
        col: colIndex,
        key,
        value,
        classKey: `${rowIndex},${colIndex}`
      });
    });
  });

  return cells;
}

export function groupCaseAddressColorCells(cells = []) {
  const groups = new Map();
  (Array.isArray(cells) ? cells : []).forEach((cell) => {
    if (!groups.has(cell.key)) groups.set(cell.key, []);
    groups.get(cell.key).push(cell);
  });
  return groups;
}

export function hslToHexCaseAddressColor(hue, saturation, lightness) {
  const h = (((Number(hue) || 0) % 360) + 360) % 360;
  const s = Math.max(0, Math.min(1, Number(saturation) || 0));
  const l = Math.max(0, Math.min(1, Number(lightness) || 0));
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return `#${[r, g, b].map(channel => Math.round((channel + m) * 255).toString(16).padStart(2, '0')).join('')}`;
}

export function getCaseAddressColorForIndex(index = 0, seed = 0) {
  const numericIndex = Math.max(0, Number(index) || 0);
  const paletteOffset = Math.abs(Math.floor(Number(seed) || 0)) % CASE_ADDRESS_COLOR_PALETTE.length;

  if (numericIndex < CASE_ADDRESS_COLOR_PALETTE.length) {
    return CASE_ADDRESS_COLOR_PALETTE[(numericIndex + paletteOffset) % CASE_ADDRESS_COLOR_PALETTE.length];
  }

  let hue = (Number(seed) + numericIndex * 137.508) % 360;
  if (hue < 28 || hue > 344) hue = (hue + 42) % 360;
  if (hue > 8 && hue < 38) hue = (hue + 34) % 360;
  return hslToHexCaseAddressColor(hue, 0.54, 0.82);
}

export function buildCaseAddressColorState(sheetState = {}, options = {}) {
  const state = {
    ...createCaseSheetState({ includeHeaders: true }),
    ...(sheetState || {}),
    data: normalizeCaseMatrix(sheetState?.data || [])
  };
  const classMap = caseCellClassMapFromState(state);

  for (const [key, className] of Array.from(classMap.entries())) {
    const tokens = caseClassTokens(className);
    if (!caseAddressColorMarkerPresent(tokens)) continue;

    const remaining = caseClassListWithoutAddressColors(tokens);
    if (remaining.length > 0) classMap.set(key, remaining.join(' '));
    else classMap.delete(key);
  }

  const cells = collectCaseAddressColorCells(state.data);
  const groups = [...groupCaseAddressColorCells(cells).entries()].sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey));
  const seed = Number.isFinite(Number(options.seed)) ? Number(options.seed) : Math.floor(Math.random() * 360);
  let coloredCellCount = 0;

  groups.forEach(([, groupCells], index) => {
    const backgroundHex = getCaseAddressColorForIndex(index, seed);
    const backgroundClass = `case-address-color-${backgroundHex.slice(1)}`;
    const textClass = 'case-address-text-0f172a';

    groupCells.forEach((cell) => {
      const tokens = new Set(caseClassListWithoutAddressColors(caseClassTokens(classMap.get(cell.classKey))));
      tokens.add(CASE_ADDRESS_COLOR_CLASS);
      tokens.add(backgroundClass);
      tokens.add(textClass);
      classMap.set(cell.classKey, [...tokens].join(' '));
      coloredCellCount += 1;
    });
  });

  const repeatedGroups = groups.filter(([, groupCells]) => groupCells.length > 1);

  return {
    state: {
      ...state,
      cellClasses: [...classMap.entries()]
    },
    seed,
    addressCellCount: cells.length,
    uniqueAddressCount: groups.length,
    repeatedAddressCount: repeatedGroups.length,
    repeatedAddressCellCount: repeatedGroups.reduce((count, [, groupCells]) => count + groupCells.length, 0),
    coloredCellCount
  };
}

export function clearCaseAddressColorState(sheetState = {}) {
  const state = {
    ...createCaseSheetState({ includeHeaders: true }),
    ...(sheetState || {}),
    data: normalizeCaseMatrix(sheetState?.data || [])
  };
  const classMap = caseCellClassMapFromState(state);
  let clearedCellCount = 0;

  for (const [key, className] of Array.from(classMap.entries())) {
    const tokens = caseClassTokens(className);
    if (!caseAddressColorMarkerPresent(tokens)) continue;

    const remaining = caseClassListWithoutAddressColors(tokens);
    if (remaining.length > 0) classMap.set(key, remaining.join(' '));
    else classMap.delete(key);
    clearedCellCount += 1;
  }

  return {
    state: {
      ...state,
      cellClasses: [...classMap.entries()]
    },
    clearedCellCount
  };
}

export function buildCaseAddressColorCoverageAnalysis(sheetState = {}, workflowState = {}, caseDataRevision = 0) {
  const state = {
    ...createCaseSheetState({ includeHeaders: true }),
    ...(sheetState || {}),
    data: normalizeCaseMatrix(sheetState?.data || [])
  };
  const cells = collectCaseAddressColorCells(state.data);
  const groups = [...groupCaseAddressColorCells(cells).entries()];
  const repeatedGroups = groups.filter(([, groupCells]) => groupCells.length > 1);
  const classMap = caseCellClassMapFromState(state);
  const colorByAddressKey = new Map();
  let coloredAddressCellCount = 0;
  let uncoloredAddressCellCount = 0;
  let conflictingColorGroupCount = 0;

  groups.forEach(([addressKey, groupCells]) => {
    const colors = new Set();

    groupCells.forEach((cell) => {
      const tokens = caseClassTokens(classMap.get(cell.classKey));
      const colorToken = [...tokens].find(token => token.startsWith('case-address-color-'))
        || [...tokens].find(token => token.startsWith('color-'))
        || '';
      if (caseAddressColorMarkerPresent(tokens) && colorToken) {
        coloredAddressCellCount += 1;
        colors.add(colorToken);
      } else {
        uncoloredAddressCellCount += 1;
      }
    });

    if (colors.size > 1) conflictingColorGroupCount += 1;
    if (colors.size === 1) colorByAddressKey.set(addressKey, [...colors][0]);
  });

  const run = workflowState?.addressColorRun && typeof workflowState.addressColorRun === 'object' ? workflowState.addressColorRun : null;
  const revision = Number(caseDataRevision) || 0;
  const runRevision = Number(run?.revision);
  const current = !!run && Number.isFinite(runRevision) && runRevision === revision;
  const complete = cells.length > 0 && current && uncoloredAddressCellCount === 0 && conflictingColorGroupCount === 0;

  return {
    addressCellCount: cells.length,
    uniqueAddressCount: groups.length,
    repeatedAddressCount: repeatedGroups.length,
    repeatedAddressCellCount: repeatedGroups.reduce((count, [, groupCells]) => count + groupCells.length, 0),
    coloredAddressCellCount,
    coloredRepeatedCellCount: coloredAddressCellCount,
    uncoloredAddressCellCount,
    conflictingColorGroupCount,
    current,
    complete,
    run,
    colorByAddressKey
  };
}

export function buildPathOperationCandidatesTsv(candidates = []) {
  const rows = [
    ['Spreadsheet Row', 'Main Path Label', 'Path Label', 'Transaction Hash', 'To Address', 'Attribution', 'Funds Followed', 'Left Bounds', 'Continues As', 'Can Prune', 'Can Graft', 'Raw Matches']
  ];
  (Array.isArray(candidates) ? candidates : []).forEach((candidate) => {
    rows.push([
      candidate.spreadsheetRowNumber,
      candidate.mainPathLabel,
      candidate.pathLabel,
      candidate.hash,
      candidate.toAddress,
      candidate.attribution,
      candidate.fundsFollowed,
      candidate.leftBoundsDisplay || candidate.leftBoundsNumeric,
      candidate.continuesAs,
      candidate.canPrune ? 'Yes' : 'No',
      candidate.canGraft ? 'Yes' : 'No',
      candidate.rawCandidates?.length || 0
    ]);
  });
  return buildTsv(rows);
}

export function buildPrunePreviewForCandidate(mainData = [], candidate = null) {
  if (!candidate) {
    return {
      valid: false,
      message: 'No candidate selected.',
      descendantEntryCount: 0,
      rowRemovalCount: 0,
      descendantHashes: [],
      rowIndexesToRemove: []
    };
  }

  const data = normalizeCaseMatrix(mainData);
  const entries = parseCaseEntries(data).filter(entry => entry.startRow >= candidate.mainBlockStartRowIndex && entry.startRow <= candidate.mainBlockContentEndRowIndex);
  const entryByHashKey = new Map(entries.map(entry => [normalizeComparisonKey(entry.hash), entry]));
  const selectedEntry = entries.find(entry => entryContainsRow(entry, candidate.rowIndex)) || null;
  const selectedEntryHashKey = normalizeComparisonKey(selectedEntry?.hash || candidate.hash);
  const queue = (candidate.childHashes || []).map(normalizeComparisonKey).filter(Boolean);
  const seen = new Set();
  const descendantEntries = [];

  while (queue.length > 0) {
    const hashKey = queue.shift();
    if (!hashKey || seen.has(hashKey) || hashKey === selectedEntryHashKey) continue;
    seen.add(hashKey);

    const childEntry = entryByHashKey.get(hashKey);
    if (!childEntry) continue;
    descendantEntries.push(childEntry);

    (childEntry.rows || []).forEach((item) => {
      parseCaseContinuesAsHashes(item?.row?.[16]).forEach(childHash => queue.push(normalizeComparisonKey(childHash)));
    });
  }

  const rowIndexesToRemove = Array.from(new Set(descendantEntries.flatMap(entry => (entry.rows || []).map(item => item.rowIndex))))
    .filter(rowIndex => rowIndex !== candidate.rowIndex)
    .sort((left, right) => left - right);
  const descendantHashes = descendantEntries.map(entry => entry.hash).filter(Boolean);
  const message = descendantEntries.length > 0
    ? `Pruning will remove downstream entries that follow ${abbreviateCaseBuddyHash(candidate.hash)} from row ${candidate.spreadsheetRowNumber}.`
    : 'No downstream entries were detected from this row. Applying prune will still mark the selected output as a path ending.';

  return {
    valid: true,
    message,
    descendantEntryCount: descendantEntries.length,
    rowRemovalCount: rowIndexesToRemove.length,
    descendantHashes,
    rowIndexesToRemove
  };
}

export function normalizePathEndSystemNotes(notes = '') {
  const cleaned = normalizeCell(notes)
    .replace(/\b(?:Continues|Branches)\s+as\s+[^.。\n]*(?:[.。]|$)/gi, ' ')
    .replace(/\bPath\s+ends\s*\.?/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned ? `${cleaned.replace(/[.。]+$/g, '')}. Path ends.` : 'Path ends.';
}

export function buildPrunedMainDataForCandidate(mainData = [], candidate = null) {
  const data = normalizeCaseMatrix(mainData);
  const preview = buildPrunePreviewForCandidate(data, candidate);
  if (!candidate || !preview.valid) {
    throw new Error(preview.message || 'No valid prune candidate selected.');
  }

  const selectedRow = data[candidate.rowIndex];
  if (!Array.isArray(selectedRow)) {
    throw new Error('The selected row no longer exists in Main Data.');
  }

  selectedRow[16] = 'Path ends.';
  selectedRow[9] = normalizePathEndSystemNotes(selectedRow[9]);

  preview.rowIndexesToRemove
    .slice()
    .sort((left, right) => right - left)
    .forEach((rowIndex) => {
      if (rowIndex >= 0 && rowIndex < data.length && rowIndex !== candidate.rowIndex) {
        data.splice(rowIndex, 1);
      }
    });

  while (data.length < CASE_DEFAULT_ROWS) {
    data.push(Array(CASE_DEFAULT_COLS).fill(''));
  }

  return { data: normalizeCaseMatrix(data), preview };
}

export function parsePreparedPathOperationEntries(rows = []) {
  const entries = [];
  let current = null;

  normalizeCaseRowsToWidth(rows).forEach((row, rowIndex) => {
    if (!rowHasMeaningfulData(row) || rowLooksLikeCaseBuddyHeader(row)) return;
    const hash = normalizeCell(row?.[1]);
    if (hash) {
      current = { hash, hashKey: normalizeComparisonKey(hash), startRow: rowIndex, rows: [{ rowIndex, row }] };
      entries.push(current);
      return;
    }
    if (current) current.rows.push({ rowIndex, row });
  });

  return entries;
}

export function buildPathOperationRawRowsFromTsv(rawTsv = '') {
  const rows = normalizeRawDataImportRowsFromPaste(rawTsv, { addHeadersIfMissing: true });
  return normalizeCaseRowsToWidth(rows).filter((row, index, source) => {
    if (rowHasMeaningfulData(row)) return true;
    const previousMeaningful = index > 0 && rowHasMeaningfulData(source[index - 1]);
    const nextMeaningful = index < source.length - 1 && rowHasMeaningfulData(source[index + 1]);
    return previousMeaningful && nextMeaningful;
  });
}

export function decorateRawSetForPathExtensionUse(rawSet = {}, candidate = null) {
  const rootRow = Array.isArray(rawSet?.rootRow)
    ? rawSet.rootRow
    : (Array.isArray(rawSet?.rows) ? rawSet.rows.find(isRawRootCandidateMatrixRow) : []);
  const matchedRootEntryRow = candidate
    ? findMatchingRawSetRootEntryRow(rawSet, candidate.row || []) || rootRow
    : rootRow;
  return { ...rawSet, rootRow, matchedRootEntryRow };
}

export function selectRawSetForPathExtension(rawRows = [], candidate = null, action = 'graft') {
  const sets = buildRawDataSetsFromMatrix(rawRows);
  const ordered = sets.slice().sort((left, right) => (right.rootRowIndex || 0) - (left.rootRowIndex || 0));
  if (ordered.length === 0) {
    throw new Error('The pasted raw TSV did not contain a usable raw data set with a root transaction hash in Column B.');
  }

  if (action === 'graft' && candidate) {
    const matched = ordered.find(rawSet => rawSetMatchesPathOperationCandidate(rawSet, candidate.row || [], candidate.hash));
    if (!matched) {
      const rootHashes = ordered.map(set => abbreviateCaseBuddyHash(set.rootHash)).filter(Boolean).join(', ');
      throw new Error(`No raw data set matched the selected graft row. Expected root hash ${abbreviateCaseBuddyHash(candidate.hash)} with matching To/Funds/Left-bounds data. Raw roots found: ${rootHashes || 'none'}.`);
    }
    return decorateRawSetForPathExtensionUse(matched, candidate);
  }

  return decorateRawSetForPathExtensionUse(ordered[0], candidate);
}

export function incrementLastAlphabeticCharacter(label = '') {
  const text = normalizeCell(label);
  if (!text) return '';
  const last = text[text.length - 1];
  if (!/[a-z]/i.test(last) || last.toLowerCase() === 'z') return '';
  return `${text.slice(0, -1)}${String.fromCharCode(last.charCodeAt(0) + 1)}`;
}

export function suggestPostBridgePathLabel(parentPathLabel = '', mainData = []) {
  const parent = normalizeCell(parentPathLabel) || '1';
  const used = new Set();
  normalizeCaseMatrix(mainData).forEach((row) => {
    const label = normalizeComparisonKey(row?.[0]);
    if (label) used.add(label);
  });

  const last = parent[parent.length - 1] || '';
  let candidate = '';
  if (/\d/.test(last)) candidate = `${parent}a`;
  else if (/[a-z]/i.test(last)) candidate = incrementLastAlphabeticCharacter(parent) || `${parent}1`;
  else candidate = `${parent}1`;
  if (candidate && !used.has(normalizeComparisonKey(candidate))) return candidate;

  if (/\d/.test(last)) {
    for (let code = 97; code <= 122; code += 1) {
      candidate = `${parent}${String.fromCharCode(code)}`;
      if (!used.has(normalizeComparisonKey(candidate))) return candidate;
    }
  }

  if (/[a-z]/i.test(last)) {
    const prefix = parent.slice(0, -1);
    const start = last.toLowerCase().charCodeAt(0) + 1;
    for (let code = start; code <= 122; code += 1) {
      candidate = `${prefix}${String.fromCharCode(code)}`;
      if (!used.has(normalizeComparisonKey(candidate))) return candidate;
    }
  }

  let suffix = 1;
  do {
    candidate = `${parent}_bridge${suffix}`;
    suffix += 1;
  } while (used.has(normalizeComparisonKey(candidate)));
  return candidate;
}

export function cleanPathOperationSystemNotes(notes = '') {
  return normalizeCell(notes)
    .replace(/\b(?:Continues|Branches)\s+as\s+[^.。\n]*(?:[.。]|$)/gi, ' ')
    .replace(/\bPath\s+ends\s*\.?/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function normalizeSystemNotesForPathOperation(existingNotes = '', processedNotes = '', continuesAs = '') {
  if (/\bpath\s+ends\b/i.test(normalizeCell(continuesAs))) {
    return normalizePathEndSystemNotes(firstNonEmptyValue(processedNotes, existingNotes));
  }

  const fragments = [];
  [existingNotes, processedNotes].forEach((notes) => {
    const cleaned = cleanPathOperationSystemNotes(notes);
    if (cleaned && !fragments.some(fragment => normalizeComparisonKey(fragment) === normalizeComparisonKey(cleaned))) {
      fragments.push(cleaned.replace(/[.。]+$/g, ''));
    }
  });
  return fragments.length > 0 ? `${fragments.join('. ')}.` : '';
}

export function findRootPatchRowForGraft(rootEntry = null, candidate = null) {
  const rows = Array.isArray(rootEntry?.rows) ? rootEntry.rows : [];
  if (rows.length === 0) return null;
  const selectedRow = candidate?.row || [];
  return rows.find(item => pathOperationOutputRowMatches(selectedRow, item?.row || []))?.row || rows[0]?.row || null;
}

export function collectDescendantRowsForSelectedGraftOutput(processedEntries = [], rootPatchRow = []) {
  const entryByHash = new Map((Array.isArray(processedEntries) ? processedEntries : [])
    .filter(entry => entry?.hashKey)
    .map(entry => [entry.hashKey, entry]));
  const queue = parseCaseContinuesAsHashes(rootPatchRow?.[16]).map(normalizeComparisonKey).filter(Boolean);
  const seen = new Set();
  const includeKeys = new Set();

  while (queue.length > 0) {
    const hashKey = queue.shift();
    if (!hashKey || seen.has(hashKey)) continue;
    seen.add(hashKey);
    const entry = entryByHash.get(hashKey);
    if (!entry) continue;
    includeKeys.add(hashKey);
    (entry.rows || []).forEach((item) => {
      parseCaseContinuesAsHashes(item?.row?.[16]).forEach(childHash => queue.push(normalizeComparisonKey(childHash)));
    });
  }

  return trimOuterEmptyRows(normalizeCaseRowsToWidth((processedEntries || [])
    .filter(entry => includeKeys.has(entry.hashKey))
    .flatMap(entry => (entry.rows || []).map(item => item.row))));
}

export function getFirstTransactionHashFromRows(rows = []) {
  for (const row of normalizeCaseRowsToWidth(rows)) {
    const hash = normalizeCell(row?.[1]);
    if (hash) return hash;
  }
  return '';
}

export function findInsertionIndexAfterPathOperationEntry(data = [], candidate = null) {
  if (!candidate) return -1;
  let insertionIndex = Math.max(0, Number(candidate.entryEndRowIndex) + 1);
  const blockEnd = Number.isInteger(candidate.mainBlockEndRowIndex) ? candidate.mainBlockEndRowIndex : data.length - 1;
  while (insertionIndex < data.length && insertionIndex <= blockEnd && !rowHasMeaningfulData(data[insertionIndex])) {
    insertionIndex += 1;
  }
  return Math.max(0, insertionIndex);
}

export function patchGraftSelectedRow(selectedRow = [], rootPatchRow = [], patchedContinuesAs = '') {
  const nextContinuesAs = firstNonEmptyValue(patchedContinuesAs, rootPatchRow?.[16], selectedRow?.[16], 'Path ends.');
  selectedRow[16] = nextContinuesAs;
  selectedRow[9] = normalizeSystemNotesForPathOperation(selectedRow?.[9], rootPatchRow?.[9], nextContinuesAs);
  selectedRow[10] = firstNonEmptyValue(selectedRow?.[10], rootPatchRow?.[10]);
  selectedRow[12] = firstNonEmptyValue(rootPatchRow?.[12], selectedRow?.[12]);
  selectedRow[17] = firstNonEmptyValue(rootPatchRow?.[17], selectedRow?.[17]);
  selectedRow[18] = firstNonEmptyValue(rootPatchRow?.[18], selectedRow?.[18]);
  selectedRow[19] = firstNonEmptyValue(selectedRow?.[19], rootPatchRow?.[19]);
  selectedRow[20] = firstNonEmptyValue(selectedRow?.[20], rootPatchRow?.[20]);
  selectedRow[21] = firstNonEmptyValue(selectedRow?.[21], rootPatchRow?.[21]);
}

export function patchBridgeRowsForMain(processedRows = [], candidate = null, bridge = {}, childLabel = '') {
  const rows = trimOuterEmptyRows(normalizeCaseRowsToWidth(processedRows));
  const postHash = getFirstTransactionHashFromRows(rows);
  const parentHash = normalizeCell(candidate?.hash);
  const destinationNetwork = normalizeCell(bridge?.destinationNetwork);
  const destinationToken = normalizeCell(bridge?.destinationToken);
  const bridgeService = normalizeCell(bridge?.service);
  let patchedLead = false;

  rows.forEach((row) => {
    if (!patchedLead && normalizeCell(row?.[1])) {
      row[0] = childLabel || normalizeCell(row?.[0]);
      row[9] = buildBridgeInSystemNote(destinationNetwork, bridgeService);
      row[10] = firstNonEmptyValue(row?.[10], bridgeService, 'Unknown');
      row[14] = parentHash;
      if (!normalizeCell(row?.[6]) && destinationToken) row[6] = destinationToken;
      if (!normalizeCell(row?.[20]) && destinationNetwork) row[20] = destinationNetwork;
      patchedLead = true;
    }
  });

  return { rows, postHash };
}

export function buildSplicedPathOperationMainData(mainData = [], candidate = null, insertRows = [], options = {}) {
  const data = normalizeCaseMatrix(mainData);
  if (!candidate || !Number.isInteger(candidate.rowIndex)) throw new Error('No selected path operation row was supplied.');
  const selectedRow = data[candidate.rowIndex];
  if (!Array.isArray(selectedRow)) throw new Error('The selected row no longer exists in Main Data.');
  if (typeof options.patchSelectedRow === 'function') options.patchSelectedRow(selectedRow, data);

  let insertionIndex = findInsertionIndexAfterPathOperationEntry(data, candidate);
  if (insertionIndex < 0 || insertionIndex > data.length) throw new Error('Could not find a safe insertion point for the selected entry.');

  let rowsToInsert = trimOuterEmptyRows(normalizeCaseRowsToWidth(insertRows));
  if (rowsToInsert.length > 0) {
    const entryEndIndex = Number.isInteger(candidate.entryEndRowIndex) ? candidate.entryEndRowIndex : candidate.rowIndex;
    const firstRowAfterEntry = Math.max(0, entryEndIndex + 1);
    const skippedSpacerCount = Math.max(0, insertionIndex - firstRowAfterEntry);
    const hasExistingSpacerBeforeInsertion = skippedSpacerCount > 0;

    if (options.forceSpacerBefore && hasExistingSpacerBeforeInsertion) {
      const firstContentRowIndex = Math.min(data.length, firstRowAfterEntry + 1);
      const extraSpacerCount = Math.max(0, insertionIndex - firstContentRowIndex);
      if (extraSpacerCount > 0) {
        data.splice(firstContentRowIndex, extraSpacerCount);
        insertionIndex -= extraSpacerCount;
      }
      insertionIndex = firstContentRowIndex;
    }

    const previousRowHasData = insertionIndex > 0 && rowHasMeaningfulData(data[insertionIndex - 1]);
    const needsGeneratedLeadingSpacer = options.forceSpacerBefore
      ? !hasExistingSpacerBeforeInsertion
      : previousRowHasData;

    if (needsGeneratedLeadingSpacer) rowsToInsert = [Array(CASE_DEFAULT_COLS).fill(''), ...rowsToInsert];
    if (insertionIndex < data.length && rowHasMeaningfulData(data[insertionIndex]) && rowHasMeaningfulData(rowsToInsert[rowsToInsert.length - 1])) {
      rowsToInsert = [...rowsToInsert, Array(CASE_DEFAULT_COLS).fill('')];
    }
    data.splice(insertionIndex, 0, ...rowsToInsert);
  }

  while (data.length < CASE_DEFAULT_ROWS) data.push(Array(CASE_DEFAULT_COLS).fill(''));
  return { data: normalizeCaseMatrix(data), insertionIndex, rowsInserted: rowsToInsert.length, contentRowsInserted: rowsToInsert.filter(rowHasMeaningfulData).length };
}

export function buildPathOperationExtensionApplyResult(mainData = [], candidate = null, rawTsv = '', options = {}) {
  if (!candidate) throw new Error('Select a path ending before applying a graft.');
  if (!candidate.isPathEnding) throw new Error('Graft and Bridge graft require a current Path ends row. Prune first if this output needs to become a path ending.');

  const action = options.action === 'bridge' ? 'bridge' : 'graft';
  const bridge = options.bridge || {};
  const rawRows = buildPathOperationRawRowsFromTsv(rawTsv);
  const rawSet = selectRawSetForPathExtension(rawRows, candidate, action);
  const inputRows = buildTool68InputRowsForRawSet(rawSet);
  if (inputRows.length <= 1) throw new Error('No usable raw rows were found for Tool 68.');

  const data = normalizeCaseMatrix(mainData);
  const childLabel = action === 'bridge' ? suggestPostBridgePathLabel(candidate.pathLabel || candidate.mainPathLabel, data) : '';
  const baseLabel = action === 'bridge' ? childLabel : firstNonEmptyValue(candidate.pathLabel, candidate.mainPathLabel, '1');
  const chainUsed = normalizeTool68ChainMode(options.chainMode || 'AUTO', rawSet.rows || rawSet.rootRow || candidate.row || []);
  const toolResult = runTool68BFlowAnalysis(inputRows, {
    chain: chainUsed,
    baseLabel,
    labelLongestPathFirst: options.labelLongestPathFirst !== false,
    pruneBTCUnfollowed: options.pruneBTCUnfollowed !== false,
    extraSpacing: !!options.extraSpacing
  });
  if (!toolResult.success) throw new Error(toolResult.feedback || 'Tool 68 did not complete successfully for this path operation.');

  const normalizedRows = normalizeTool68ProcessedRowsForMainRewrite({
    leadRow: rawSet.rootRow || candidate.row || [],
    rows: [rawSet.rootRow || candidate.row || []]
  }, toolResult.outputRows, { baseLabel });
  const processedEntries = parsePreparedPathOperationEntries(normalizedRows);
  if (processedEntries.length === 0) throw new Error('Tool 68 did not produce any transaction entries to apply.');

  let rowsToInsert = [];
  let patchedContinuesAs = '';
  let postBridgeHash = '';
  let rootPatchRow = null;
  let message = '';

  if (action === 'bridge') {
    const patchedBridge = patchBridgeRowsForMain(normalizedRows, candidate, bridge, childLabel);
    rowsToInsert = patchedBridge.rows;
    postBridgeHash = patchedBridge.postHash;
    if (!postBridgeHash) throw new Error('Bridge graft requires the prepared raw data to contain a post-bridge transaction hash.');
    if (normalizeComparisonKey(postBridgeHash) === normalizeComparisonKey(candidate.hash)) {
      throw new Error('Bridge graft raw data should begin with the post-bridge transaction hash, not the selected bridge-out transaction hash. Use normal graft for same-transaction extensions.');
    }
    patchedContinuesAs = `Continues as ${postBridgeHash}`;
    message = `Bridge graft applied beneath row ${candidate.spreadsheetRowNumber}. The parent row now continues as ${postBridgeHash}.`;
  } else {
    const rootEntry = processedEntries.find(entry => normalizeComparisonKey(entry.hash) === normalizeComparisonKey(candidate.hash)) || processedEntries[0];
    if (normalizeComparisonKey(rootEntry?.hash) !== normalizeComparisonKey(candidate.hash)) {
      throw new Error(`The Tool 68 root entry ${abbreviateCaseBuddyHash(rootEntry?.hash)} does not match the selected graft transaction ${abbreviateCaseBuddyHash(candidate.hash)}.`);
    }
    rootPatchRow = findRootPatchRowForGraft(rootEntry, candidate);
    if (!rootPatchRow) throw new Error('Could not find the root output row to patch the selected graft target.');
    rowsToInsert = collectDescendantRowsForSelectedGraftOutput(processedEntries, rootPatchRow);
    const firstInsertedHash = getFirstTransactionHashFromRows(rowsToInsert);
    patchedContinuesAs = firstNonEmptyValue(rootPatchRow?.[16], firstInsertedHash ? `Continues as ${firstInsertedHash}` : 'Path ends.');
    const expectedChildHashes = parseCaseContinuesAsHashes(patchedContinuesAs).map(normalizeComparisonKey).filter(Boolean);
    const insertedHashKeys = new Set(rowsToInsert.map(row => normalizeComparisonKey(row?.[1])).filter(Boolean));
    const missingChildren = expectedChildHashes.filter(hashKey => !insertedHashKeys.has(hashKey));
    if (missingChildren.length > 0) {
      throw new Error('Tool 68 says the selected output continues, but one or more downstream transaction rows were not available after removing the duplicate root entry.');
    }
    message = rowsToInsert.length > 0
      ? `Graft applied beneath row ${candidate.spreadsheetRowNumber}. Inserted ${rowsToInsert.filter(rowHasMeaningfulData).length} downstream row(s).`
      : `Graft processed row ${candidate.spreadsheetRowNumber}; no downstream rows were added because the selected output still ends there.`;
  }

  const spliceResult = buildSplicedPathOperationMainData(data, candidate, rowsToInsert, {
    forceSpacerBefore: action === 'bridge',
    patchSelectedRow: (selectedRow) => {
      if (action === 'bridge') {
        selectedRow[9] = buildBridgeOutSystemNote(bridge.destinationNetwork, bridge.service);
        selectedRow[10] = firstNonEmptyValue(bridge.service, selectedRow?.[10]);
        selectedRow[16] = patchedContinuesAs;
        return;
      }
      patchGraftSelectedRow(selectedRow, rootPatchRow, patchedContinuesAs);
    }
  });

  return {
    ...spliceResult,
    action,
    rawSet,
    rawSelection: { rawRowsForRawSheet: rawRows, rawSetSignature: buildRawSetSignature(rawSet) },
    splice: { insertedSpreadsheetRows: spliceResult.rowsInserted, insertedRows: spliceResult.contentRowsInserted, insertionSpreadsheetRow: spliceResult.insertionIndex + 1 },
    rawSetSignature: buildRawSetSignature(rawSet),
    chainUsed,
    baseLabel,
    childLabel,
    rootPatchRow,
    patchedContinuesAs,
    postBridgeHash,
    message,
    toolResult,
    toolFeedback: toolResult.feedback || 'Tool 68 completed.',
    tool68Rows: normalizedRows.filter(rowHasMeaningfulData).length,
    downstreamRows: trimOuterEmptyRows(rowsToInsert).filter(rowHasMeaningfulData).length
  };
}

export function buildPathOperationAuditRows({
  action = '',
  candidate = null,
  preview = null,
  rawTsv = '',
  bridge = {},
  stagedOnly = false,
  extensionResult = null,
  splice = null,
  rawAppend = null
} = {}) {
  const appliedExtension = extensionResult || null;
  const spliceSummary = splice || appliedExtension?.splice || null;
  const rawAppendSummary = rawAppend || appliedExtension?.rawAppend || null;
  const rows = [
    CASE_COLUMN_HEADERS.slice(),
    ['Path Operation Audit', '', '', '', new Date().toISOString(), '', '', '', '', stagedOnly ? 'Staged only. No Main Data mutation was applied.' : 'Main Data mutation applied.', '', '', '', '', '', '', '', '', '', '', '', '']
  ];

  if (candidate) {
    rows.push([
      candidate.pathLabel || candidate.mainPathLabel || '',
      candidate.hash || '',
      candidate.fromAddress || '',
      candidate.toAddress || '',
      candidate.utcDate || '',
      candidate.amount || '',
      candidate.token || '',
      '',
      '',
      `${action.toUpperCase()} target row ${candidate.spreadsheetRowNumber}.`,
      candidate.attribution || '',
      candidate.analystNotes || '',
      candidate.fundsFollowed || '',
      candidate.priorBalance || '',
      candidate.fromHash || '',
      '',
      candidate.continuesAs || '',
      candidate.leftBoundsDisplay || '',
      candidate.leftBoundsNumeric || '',
      candidate.chainId || '',
      candidate.chainName || '',
      candidate.hopLogic || ''
    ]);
  }

  if (preview) {
    rows.push(['Preview', '', '', '', '', '', '', '', '', preview.message || '', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Descendant entries', preview.descendantEntryCount ?? '', '', '', '', '', '', '', '', 'Downstream entries affected by prune.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Rows removed', preview.rowRemovalCount ?? '', '', '', '', '', '', '', '', 'Spreadsheet rows affected by prune.', '', '', '', '', '', '', '', '', '', '', '', '']);
    if (Array.isArray(preview.descendantHashes) && preview.descendantHashes.length > 0) {
      rows.push(['Descendant hashes', preview.descendantHashes.join(', '), '', '', '', '', '', '', '', 'Hashes below the selected output.', '', '', '', '', '', '', '', '', '', '', '', '']);
    }
  }

  if (appliedExtension || spliceSummary) {
    rows.push(['Apply result', '', '', '', '', '', '', '', '', appliedExtension?.message || 'Path extension applied.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Tool 68 rows', appliedExtension?.tool68Rows ?? '', '', '', '', '', '', '', '', appliedExtension?.toolFeedback || 'Tool 68 processed the selected raw data.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Rows inserted', spliceSummary?.insertedSpreadsheetRows ?? appliedExtension?.rowsInserted ?? '', '', '', '', '', '', '', '', 'Spreadsheet rows inserted into Main Data, including spacer rows when applicable.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Meaningful rows inserted', spliceSummary?.insertedRows ?? appliedExtension?.contentRowsInserted ?? '', '', '', '', '', '', '', '', 'Non-empty downstream / post-bridge rows inserted.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Insertion row', spliceSummary?.insertionSpreadsheetRow ?? '', '', '', '', '', '', '', '', 'One-based spreadsheet row where the extension was inserted.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Base label used', appliedExtension?.baseLabel || '', '', '', '', '', '', '', '', action === 'bridge' ? 'Suggested post-bridge base label.' : 'Base label used for Tool 68 graph labeling.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Patched Column Q', appliedExtension?.patchedContinuesAs || '', '', '', '', '', '', '', '', 'Updated Continues As / Path Ends value on the selected output row.', '', '', '', '', '', '', '', '', '', '', '', '']);
    if (appliedExtension?.postBridgeHash) {
      rows.push(['Post-bridge hash', appliedExtension.postBridgeHash, '', '', '', '', '', '', '', 'First transaction hash in the inserted post-bridge flow.', '', '', '', '', '', '', '', '', '', '', '', '']);
    }
  }

  if (action === 'bridge') {
    rows.push(['Bridge service', bridge.service || '', '', '', '', '', '', '', '', stagedOnly ? 'Bridge graft metadata staged for later splice.' : 'Bridge service applied to bridge-out / bridge-in notes.', '', '', '', '', '', '', '', '', '', '', '', '']);
    if (bridge.registryMatch?.canonicalName) {
      rows.push(['Bridge registry match', bridge.registryMatch.canonicalName, bridge.registryMatch.matchedAlias || '', '', '', '', '', '', '', bridge.registryMatch.searchHint || 'Known bridge service profile used.', '', '', '', '', '', '', '', bridge.registryMatch.explorerUrl || '', '', '', '', '']);
    }
    rows.push(['Destination network', bridge.destinationNetwork || '', '', '', '', '', '', '', '', 'Destination network for bridge note language.', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Destination token', bridge.destinationToken || '', '', '', '', '', '', '', '', 'Destination token if known.', '', '', '', '', '', '', '', '', '', '', '', '']);
  }

  if (rawTsv) {
    rows.push(['Raw TSV supplied', `${rawTsv.split(/\r?\n/).filter(line => line.trim()).length} non-empty line(s)`, '', '', '', '', '', '', '', stagedOnly ? 'Raw TSV captured for graft/Bridge staging.' : 'Raw TSV used for graft/Bridge apply.', '', '', '', '', '', '', '', '', '', '', '', '']);
  }

  if (rawAppendSummary && rawAppendSummary.rowsAppended > 0) {
    rows.push(['Raw Data preserved', rawAppendSummary.rowsAppended, '', '', '', '', '', '', '', `Manual raw TSV appended to Raw Data starting at row ${rawAppendSummary.startRow + 1}.`, '', '', '', '', '', '', '', '', '', '', '', '']);
  }

  return rows;
}
